import { computed, reactive, ref } from 'vue'
import { notify } from '@staff/ui'

import { ApiRequestError } from '@/services/http'
import {
  fetchKycQuestionStatus,
  fetchKycSuggestedAnswers,
  submitKycAnswers,
  type KycAnswers,
  type KycQuestionStatus,
} from '@/services/kyc'
import { fetchUserById, type UserDetails } from '@/services/user'
import {
  emptyKycFormValues,
  prefillKycForm,
  serverMessagesByQuestion,
  toKycAnswers,
  withEmploymentStatus,
  type KycFormPrefill,
} from './kycForm'

interface UseKycAnswersFormOptions {
  kycId: string
  userId: string
  /** Runs after the server took the answers, before `onDone`. */
  onSubmitted: () => unknown
  onDone: () => void
}

const noServerMessages = (): ReturnType<typeof serverMessagesByQuestion> => ({ byQuestion: {}, other: [] })

/**
 * Loads one KYC cycle for answering and submits the form. A cycle that no longer awaits answers loads
 * read-only, also when that is only found out on submit (409). Starts loading at once.
 *
 * Logs carry the KYC id and the HTTP status only, never an answer.
 */
export const useKycAnswersForm = (options: UseKycAnswersFormOptions) => {
  const isLoading = ref(true)
  const loadError = ref('')
  const isSubmitting = ref(false)
  const user = ref<UserDetails | null>(null)
  const questionStatus = ref<KycQuestionStatus | null>(null)
  const cycleAnswers = ref<Partial<KycAnswers>>({})
  const prefill = ref<KycFormPrefill>(prefillKycForm({}, null, ''))
  const form = reactive(emptyKycFormValues())
  const serverMessages = ref(noServerMessages())

  const isEditable = computed(() => questionStatus.value?.status === 'awaiting_answers')

  const load = async () => {
    isLoading.value = true
    loadError.value = ''

    try {
      const [status, loadedUser] = await Promise.all([
        fetchKycQuestionStatus(options.kycId),
        fetchUserById(options.userId),
      ])
      questionStatus.value = status
      user.value = loadedUser
      cycleAnswers.value = loadedUser.kycs.find((kyc) => kyc.kycId === options.kycId)?.answers ?? {}

      if (status.status === 'awaiting_answers') {
        prefill.value = prefillKycForm(
          cycleAnswers.value,
          await fetchKycSuggestedAnswers(options.kycId),
          loadedUser.personalNumber,
        )
        Object.assign(form, prefill.value.values)
      }
    } catch (err) {
      console.error('Failed to load KYC', {
        kycId: options.kycId,
        status: err instanceof ApiRequestError ? err.status : undefined,
      })
      loadError.value = 'KYC:n kunde inte hämtas. Försök igen.'
    } finally {
      isLoading.value = false
    }
  }

  const setEmploymentStatus = (status: string) => Object.assign(form, withEmploymentStatus(form, status))

  /** Call once the form validates. */
  const submit = async () => {
    isSubmitting.value = true
    serverMessages.value = noServerMessages()

    try {
      await submitKycAnswers(options.kycId, toKycAnswers(form))
      notify({ type: 'success', title: 'KYC-svaren skickades' })
      await options.onSubmitted()
      options.onDone()
    } catch (err) {
      const status = err instanceof ApiRequestError ? err.status : undefined
      console.error('KYC answers submit failed', { kycId: options.kycId, status })

      if (status === 409) {
        notify({
          type: 'error',
          title: 'KYC:n väntar inte längre på svar',
          description: 'Den har lästs in på nytt. Inga svar sparades.',
          dismissable: true,
        })
        await load()
      } else if (status === 400 && err instanceof ApiRequestError) {
        serverMessages.value = serverMessagesByQuestion(err.message)
        notify({
          type: 'error',
          title: 'Några svar godtogs inte',
          description: 'Se meddelandena i formuläret.',
          dismissable: true,
        })
      } else {
        notify({
          type: 'error',
          title: 'KYC-svaren kunde inte skickas',
          description: 'Ett fel uppstod. Försök igen.',
          dismissable: true,
        })
      }
    } finally {
      isSubmitting.value = false
    }
  }

  void load()

  return {
    isLoading,
    loadError,
    isSubmitting,
    isEditable,
    user,
    questionStatus,
    cycleAnswers,
    prefill,
    form,
    serverMessages,
    load,
    setEmploymentStatus,
    submit,
  }
}
